import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {message} from 'antd';
import {recycleService} from '@/services/recycleService';
import type {
  CreateRecycleCategoryCommand,
  RecycleCategory,
  RecycleCategoryQuery,
  UpdateRecycleCategoryCommand
} from '@/types/recycle';

// 异步操作
export const fetchCategories = createAsyncThunk(
  'recycleCategory/fetchCategories',
  async (query: RecycleCategoryQuery) => {
    const response = await recycleService.getCategories(query);
    return response;
  }
);

export const fetchCategoryTree = createAsyncThunk(
  'recycleCategory/fetchCategoryTree',
  async () => {
    const response = await recycleService.getCategoryTree();
    return response;
  }
);

export const fetchCategoryById = createAsyncThunk(
  'recycleCategory/fetchCategoryById',
  async (id: number) => {
    const response = await recycleService.getCategoryById(id);
    return response;
  }
);

export const createCategory = createAsyncThunk(
  'recycleCategory/createCategory',
  async (data: CreateRecycleCategoryCommand) => {
    const response = await recycleService.createCategory(data);
    return response;
  }
);

export const updateCategory = createAsyncThunk(
  'recycleCategory/updateCategory',
  async ({ id, data }: { id: number; data: UpdateRecycleCategoryCommand }) => {
    const response = await recycleService.updateCategory(id, data);
    return response;
  }
);

export const deleteCategory = createAsyncThunk(
  'recycleCategory/deleteCategory',
  async (id: number) => {
    await recycleService.deleteCategory(id);
    return id;
  }
);

export const updateCategoryStatus = createAsyncThunk(
  'recycleCategory/updateCategoryStatus',
  async ({ id, enabled }: { id: number; enabled: boolean }) => {
    const response = await recycleService.updateCategoryStatus(id, enabled);
    return response;
  }
);

// 状态类型
interface RecycleCategoryState {
  list: RecycleCategory[];
  tree: RecycleCategory[];
  currentCategory: RecycleCategory | null;
  total: number;
  loading: boolean;
  error: string | null;
}

// 初始状态
const initialState: RecycleCategoryState = {
  list: [],
  tree: [],
  currentCategory: null,
  total: 0,
  loading: false,
  error: null,
};

// Slice
const recycleCategorySlice = createSlice({
  name: 'recycleCategory',
  initialState,
  reducers: {
    clearCurrentCategory: (state) => {
      state.currentCategory = null;
    },
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchCategories
    builder
      .addCase(fetchCategories.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategories.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload.items;
        state.total = action.payload.total;
      })
      .addCase(fetchCategories.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取品类列表失败';
        message.error(state.error);
      });

    // fetchCategoryTree
    builder
      .addCase(fetchCategoryTree.pending, (state) => {
        state.error = null;
      })
      .addCase(fetchCategoryTree.fulfilled, (state, action) => {
        state.tree = action.payload;
      })
      .addCase(fetchCategoryTree.rejected, (state, action) => {
        state.error = action.error.message || '获取品类树失败';
        message.error(state.error);
      });

    // fetchCategoryById
    builder
      .addCase(fetchCategoryById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCategoryById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentCategory = action.payload;
      })
      .addCase(fetchCategoryById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '获取品类详情失败';
        message.error(state.error);
      });

    // createCategory
    builder
      .addCase(createCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.list.unshift(action.payload);
        state.total += 1;
        message.success('创建品类成功');
      })
      .addCase(createCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '创建品类失败';
        message.error(state.error);
      });

    // updateCategory
    builder
      .addCase(updateCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateCategory.fulfilled, (state, action) => {
        state.loading = false;
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentCategory?.id === action.payload.id) {
          state.currentCategory = action.payload;
        }
        message.success('更新品类成功');
      })
      .addCase(updateCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '更新品类失败';
        message.error(state.error);
      });

    // deleteCategory
    builder
      .addCase(deleteCategory.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteCategory.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter(item => item.id !== action.payload);
        state.total -= 1;
        message.success('删除品类成功');
      })
      .addCase(deleteCategory.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || '删除品类失败';
        message.error(state.error);
      });

    // updateCategoryStatus
    builder
      .addCase(updateCategoryStatus.pending, (state) => {
        state.error = null;
      })
      .addCase(updateCategoryStatus.fulfilled, (state, action) => {
        const index = state.list.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.list[index] = action.payload;
        }
        if (state.currentCategory?.id === action.payload.id) {
          state.currentCategory = action.payload;
        }
        message.success('更新状态成功');
      })
      .addCase(updateCategoryStatus.rejected, (state, action) => {
        state.error = action.error.message || '更新状态失败';
        message.error(state.error);
      });
  },
});

export const { clearCurrentCategory, clearError } = recycleCategorySlice.actions;
export default recycleCategorySlice.reducer;
